import {
  type GraphOverlay,
  type GraphParameters,
  type GraphPathLookupParameters,
  type SnapshotId,
  isCompareGraphOverlay,
  isGraphPathLookupHostToDefaultGWParameters,
  isIntentGraphOverlay,
  isPathLookupRequestParameters,
  isPathLookupTcpL4Options,
  isPathLookupUdpL4Options,
  isTopologyRequestParameters,
  safeAbsurd,
  untitledSnapshotName,
} from '@ipfabric/utils';
import {
  type GraphBreadcrumb,
  buildBreadcrumbs,
} from 'components/Graph/GraphBreadcrumbs/helpers/buildBreadcrumbs';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { useMemo } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { GraphPathWarnings } from '../model/selectors/graphMetadata/selectPathWarnings';

const pathLookupRequestToBreadcrumbsInput = (
  appliedPathLookup: GraphPathLookupParameters | null,
) => {
  if (appliedPathLookup === null) {
    return null;
  }

  const [srcPorts, dstPorts] =
    !isGraphPathLookupHostToDefaultGWParameters(appliedPathLookup) &&
    appliedPathLookup.l4Options &&
    (isPathLookupTcpL4Options(
      appliedPathLookup.protocol,
      appliedPathLookup.l4Options,
    ) ||
      isPathLookupUdpL4Options(
        appliedPathLookup.protocol,
        appliedPathLookup.l4Options,
      ))
      ? [
          appliedPathLookup.l4Options.srcPorts,
          appliedPathLookup.l4Options.dstPorts,
        ]
      : [null, null];

  switch (appliedPathLookup.pathLookupType) {
    case 'hostToDefaultGW':
      return {
        source: appliedPathLookup.startingPoint,
        sourcePort: null,
        destination: 'default-gateway',
        destinationPort: null,
        pathLookupType: appliedPathLookup.pathLookupType,
      };
    case 'multicast':
      return {
        source: appliedPathLookup.source,
        sourcePort: srcPorts,
        destination: appliedPathLookup.group,
        destinationPort: dstPorts,
        pathLookupType: appliedPathLookup.pathLookupType,
      };
    case 'unicast':
      return {
        source: appliedPathLookup.startingPoint,
        sourcePort: srcPorts,
        destination: appliedPathLookup.destinationPoint,
        destinationPort: dstPorts,
        pathLookupType: appliedPathLookup.pathLookupType,
      };
    default:
      safeAbsurd(appliedPathLookup, 'Unknown path lookup type');
      return null;
  }
};

export const useGraphBreadcrumbs = (
  parameters: GraphParameters | null,
  snapshotId: SnapshotId,
  overlay: GraphOverlay | null,
  loadedViewName: string | null,
  pathWarnings?: GraphPathWarnings,
): GraphBreadcrumb[] => {
  const snapshots = useSnapshots();

  const getSnapshotName = useStableCallback(
    (snapshotIdToGetName: SnapshotId) =>
      // intentional || over ?? - show untitled on empty string name too
      snapshots.find((s) => s.id === snapshotIdToGetName)?.name ||
      untitledSnapshotName,
  );

  return useMemo(
    () =>
      buildBreadcrumbs({
        compare:
          snapshotId && isCompareGraphOverlay(overlay)
            ? {
                snapshotName: getSnapshotName(snapshotId),
                snapshotToCompareName: getSnapshotName(
                  overlay.snapshotToCompare,
                ),
              }
            : null,
        groupBy: parameters?.groupBy ?? null,
        intentRuleName: isIntentGraphOverlay(overlay)
          ? overlay.intentRule.name
          : null,
        pathLookup: pathLookupRequestToBreadcrumbsInput(
          isPathLookupRequestParameters(parameters) ? parameters : null,
        ),
        paths: isTopologyRequestParameters(parameters) ? parameters.paths : [],
        pathWarnings: pathWarnings ?? {
          hiddenPaths: new Set(),
          missingPaths: new Set(),
          emptyPaths: new Set(),
        },
        loadedViewName,
      }),
    [parameters, snapshotId, overlay, pathWarnings, loadedViewName],
  );
};
